import { forwardRef, useCallback } from 'react';
import { Input as AntdInput, type InputProps, type InputRef } from 'antd';
import type { SearchProps, TextAreaProps } from 'antd/es/input';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { sanitizeInput } from '@/lib/utils';

/**
 * Sanitize a text field's value in place. Leaves the element untouched when
 * nothing was stripped, so normal typing never moves the caret. When text is
 * stripped, keeps the caret at the same distance from the end of the value.
 */
function sanitizeFieldValue(target: HTMLInputElement | HTMLTextAreaElement): void {
  const raw = target.value;
  const sanitized = sanitizeInput(raw);
  if (sanitized === raw) return;
  // selectionEnd is null on types without a caret API (email, number) — those throw on setSelectionRange
  const { selectionEnd } = target;
  target.value = sanitized;
  if (selectionEnd === null) return;
  const fromEnd = raw.length - selectionEnd;
  const caret = Math.max(0, sanitized.length - fromEnd);
  target.setSelectionRange(caret, caret);
}

export const SanitizedInput = forwardRef<InputRef, InputProps>(({ onChange, ...props }, ref) => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      sanitizeFieldValue(e.target);
      onChange?.(e);
    },
    [onChange],
  );

  return <AntdInput ref={ref} {...props} onChange={handleChange} />;
});

SanitizedInput.displayName = 'SanitizedInput';

export const SanitizedTextArea = forwardRef<TextAreaRef, TextAreaProps>(
  ({ onChange, ...props }, ref) => {
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        sanitizeFieldValue(e.target);
        onChange?.(e);
      },
      [onChange],
    );

    return <AntdInput.TextArea ref={ref} {...props} onChange={handleChange} />;
  },
);

SanitizedTextArea.displayName = 'SanitizedTextArea';

export const SanitizedSearch = forwardRef<InputRef, SearchProps>(
  ({ onChange, onSearch, ...props }, ref) => {
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        sanitizeFieldValue(e.target);
        onChange?.(e);
      },
      [onChange],
    );

    const handleSearch = useCallback(
      (
        value: string,
        event?:
          | React.ChangeEvent<HTMLInputElement>
          | React.MouseEvent<HTMLElement>
          | React.KeyboardEvent<HTMLInputElement>,
        info?: { source?: 'clear' | 'input' },
      ) => {
        onSearch?.(sanitizeInput(value), event, info);
      },
      [onSearch],
    );

    return (
      <AntdInput.Search ref={ref} {...props} onChange={handleChange} onSearch={handleSearch} />
    );
  },
);

SanitizedSearch.displayName = 'SanitizedSearch';
